const { sourceChargeCode, COMPUTATION_TYPES } = require('pando-constants');
const convertWeight = require('../../helpers/weightConverter');
const convertVolume = require('../../helpers/volumeConverter');
const { freightUnit } = require('../../constants/payments');
const Helper = require('./utils/helper');

const PACKAGE_UNITS = [freightUnit.per_package, freightUnit.per_case, freightUnit.per_box];

class CustomSourceCharge {
  calculateQuantity(materials = [], chargeUnit = '', materialMap, quantityUnitsForPieces) {
    if (!materials || !materials.length) return 0;
    if (chargeUnit === freightUnit.per_trip) return 1;
    if (chargeUnit === freightUnit.per_ton) {
      return materials.reduce((acc, m) => acc + convertWeight(m.weightUnit, 't', m.weight), 0);
    }
    if (chargeUnit === freightUnit.per_kg) {
      return materials.reduce((acc, m) => acc + convertWeight(m.weightUnit, 'kg', m.weight), 0);
    }
    if (chargeUnit === freightUnit.per_cft) {
      return materials.reduce((acc, m) => acc + convertVolume(m.volumeUnit, 'cft', m.volume), 0);
    }
    if (PACKAGE_UNITS.includes(chargeUnit)) {
      return materials.reduce((acc, m) => {
        const qty = Math.ceil(m.quantity || 0);
        const quantityUnit = m.quantityUnit && m.quantityUnit.toLowerCase();
        if (quantityUnit && quantityUnitsForPieces[quantityUnit]) {
          const detail = materialMap[m.materialCode];
          return detail && detail.pce_box ? acc + Math.ceil(qty / Math.ceil(detail.pce_box)) : acc;
        }
        return acc + qty;
      }, 0);
    }
    return 0;
  }

  computeChargeValue(chargeItem = {}, materials = [], materialMap = {}, quantityUnitsForPieces = {}) {
    const { charge = 0, min_charge = 0, charge_unit: chargeUnit } = chargeItem;
    const quantity = this.calculateQuantity(materials, chargeUnit, materialMap, quantityUnitsForPieces);
    const computed = chargeUnit === freightUnit.per_trip ? charge : charge * quantity;
    return Math.max(computed, min_charge);
  }

  perform(masterData = {}, strategy = []) {
    const {
      indentSources = [],
      indentSourceCharges = [],
      destinations = [],
      clientConfiguration = {},
      materials: { masterData: materialMasterData = [] } = {},
      customQuantityUnits,
    } = masterData;

    const materialMap = Helper.fetchMaterialMap(materialMasterData);
    const quantityUnitsForPieces = Helper.getQuantityUnitsForPieces(customQuantityUnits);
    const chargeComputeMethodMap = new Map();
    const chargesHash = new Map();
    const chargeOutputHash = {};
    const stdSourceCharges = Object.values(sourceChargeCode);
    const chargeCodeMap = {};

    (clientConfiguration.allowable_source_charge_codes || []).forEach((charge) => {
      chargeComputeMethodMap.set(charge.name, charge.compute_method);
      chargeCodeMap[charge.name] = charge;
    });

    const sourceGateMap = indentSources.reduce((acc, { location_id, gate_id }) => {
      if (location_id) acc[location_id.toString()] = gate_id;
      return acc;
    }, {});

    indentSourceCharges.forEach(({ source_location_id, source_gate_id, charges = [], charge_code }) => {
      if ((source_location_id || source_gate_id) && !stdSourceCharges.includes(charge_code) && strategy.includes(charge_code)) {
        const resolvedGateId = source_gate_id || (source_location_id && sourceGateMap[source_location_id.toString()]);
        const key = `${source_location_id}-${resolvedGateId || ''}`;
        if (!chargesHash.has(key)) {
          chargesHash.set(key, []);
        }
        chargesHash.get(key).push({ charges, charge_code });
      }
    });

    indentSources.forEach(({ location_id, gate_id }) => {
      if (location_id) {
        const key = `${location_id}-${gate_id || ''}`;
        const chargesList = chargesHash.get(key);

        if (chargesList) {
          const sourceMaterials = destinations
            .filter(d => d.sourceLocationId && d.sourceLocationId.toString() === location_id.toString())
            .flatMap(d => Helper.getNormalizedMaterialDetails(d.materials)
              .filter(m => !gate_id || (m.source_gate_id && m.source_gate_id.toString() === gate_id.toString())));

          chargesList.forEach((charge) => {
            const values = (charge.charges || []).map(c =>
              this.computeChargeValue(c, sourceMaterials, materialMap, quantityUnitsForPieces));
            const computeMethod = chargeComputeMethodMap.get(charge.charge_code);

            if (computeMethod === COMPUTATION_TYPES.MAX) {
              chargeOutputHash[charge.charge_code] = Math.max(chargeOutputHash[charge.charge_code] || 0, ...values, 0);
            } else if (computeMethod === COMPUTATION_TYPES.ADD) {
              chargeOutputHash[charge.charge_code] = (chargeOutputHash[charge.charge_code] || 0) + values.reduce((a, b) => a + b, 0);
            }
          });
        }
      }
    });

    this.chargeOutputHash = Object.keys(chargeOutputHash).map((name) => {
      const charge = chargeCodeMap[name];
      return {
        name,
        label: charge.label,
        amount: chargeOutputHash[name],
        master_type: 'Source',
      };
    });
    return this.chargeOutputHash;
  }
}
module.exports = CustomSourceCharge;
